import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/auth";
import { useToast } from "@/components/ui/use-toast";
import { MentorSession, MentorSessionStats } from "@/types/mentorSessions";
import { getMentorId, getMentorSessions, getPlayerProfiles } from "@/services/mentorSessionsService";
import { formatSessions, calculateStats } from "@/utils/mentorSessionsUtils";
import { supabase } from "@/integrations/supabase/client";

export type { MentorSession, MentorSessionStats } from "@/types/mentorSessions";

export const useMentorSoldSessions = () => {
  const [sessions, setSessions] = useState<MentorSession[]>([]);
  const [stats, setStats] = useState<MentorSessionStats>({
    totalSessions: 0,
    totalValue: 0,
    pendingSessions: 0,
    paidSessions: 0,
    totalNetValue: 0,
    completedSessions: 0,
    scheduledSessions: 0,
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchSessions = async (forceRefresh = false) => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Log apenas em desenvolvimento
      if (import.meta.env.DEV) {
        console.log("🔄 [useMentorSoldSessions] Fetching sessions", forceRefresh ? "(FORCE REFRESH)" : "");
      }
      
      // Get the mentor ID
      const mentorId = await getMentorId(user.id);
        
      if (!mentorId) {
        setSessions([]);
        setStats({
          totalSessions: 0,
          totalValue: 0,
          pendingSessions: 0,
          paidSessions: 0,
          totalNetValue: 0,
          completedSessions: 0,
          scheduledSessions: 0,
        });
        setLoading(false);
        return;
      }
      
      // Get mentor commission rate
      let commissionRate = 0.2; // Default 20%
      try {
        const { data: mentorData, error: mentorError } = await supabase
          .from('mentor_info')
          .select('commission_rate')
          .eq('profile_id', user.id)
          .single();
          
        if (!mentorError && mentorData?.commission_rate !== null && mentorData?.commission_rate !== undefined) {
          commissionRate = mentorData.commission_rate;
        }
      } catch (error) {
        // Silenciosamente usar default em caso de erro
      }
      
      // Get all sessions for this mentor
      const sessionData = await getMentorSessions(mentorId);

      if (!sessionData || sessionData.length === 0) {
        setSessions([]);
        setStats({
          totalSessions: 0,
          totalValue: 0,
          pendingSessions: 0,
          paidSessions: 0,
          totalNetValue: 0,
          completedSessions: 0,
          scheduledSessions: 0,
        });
        setLoading(false);
        return;
      }

      // OTIMIZAÇÃO: Removido loop de verificação de pagamentos - muito lento
      // A verificação de pagamentos deve ser feita em background ou apenas quando necessário
      // OTIMIZAÇÃO: Removida dupla busca - usar apenas uma busca de sessões
      
      // Get player profiles
      const playerIds = sessionData
        .filter(s => s.player_profile_id)
        .map(s => s.player_profile_id);
      
      const playerProfilesMap = await getPlayerProfiles(playerIds);
        
      // Format sessions and calculate stats with commission rate
      const formattedSessions = formatSessions(sessionData, playerProfilesMap);
      const sessionStats = calculateStats(formattedSessions, commissionRate);

      // Log apenas em desenvolvimento para reduzir overhead
      if (import.meta.env.DEV) {
        console.log("✅ [useMentorSoldSessions] Formatted sessions:", formattedSessions.length);
        console.log("📊 [useMentorSoldSessions] Stats:", sessionStats);
      }
      
      setSessions(formattedSessions);
      setStats(sessionStats);
      
    } catch (error: any) {
      console.error("Error fetching mentor sessions:", error.message);
      toast({
        variant: "destructive",
        title: "Erro ao carregar sessões",
        description: "Não foi possível carregar suas sessões. Tente novamente mais tarde."
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    
    // OTIMIZAÇÃO: Aumentar intervalo de refresh de 30s para 60s para reduzir carga
    const refreshInterval = setInterval(fetchSessions, 60000);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [user, toast]);

  return { sessions, stats, loading, refetch: fetchSessions };
};
